/**
 * Chat API client — sends Bible questions to the SKJV API.
 *
 * Two modes:
 *   fetchChatResponse()          — JSON request/response (CLI, fallback)
 *   fetchChatResponseStreaming()  — SSE with per-step progress events
 */

import type { ChatEvent, ReceiptItem, CommentaryRunMeta } from '@skjv/common';
import { apiPath } from '@utils/api-base';
import { parseSSEStream } from './sse-parser';
import { QueueFullError } from './queue-full-error';
import type { ChatApiResponse, ChatRequestOptions, PingResponse } from './chat-types';

export type { ChatApiResponse, ChatRequestOptions, PingResponse };
export { QueueFullError };

export interface StreamingOptions extends ChatRequestOptions {
  onProgress: (event: ChatEvent) => void;
}

/**
 * Sends a chat message to the API (JSON mode).
 */
export async function fetchChatResponse(
  message: string,
  options: ChatRequestOptions = {},
): Promise<ChatApiResponse> {
  const { includeCommentary, conversationId, deviceId } = options;
  const response = await fetch(apiPath('v1/chat'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, includeCommentary, conversationId, deviceId }),
  });

  if (!response.ok) throw new Error(`Chat API error: ${response.status}`);
  return (await response.json()) as ChatApiResponse;
}

/**
 * Sends a chat message via SSE for real-time pipeline progress.
 */
export async function fetchChatResponseStreaming(
  message: string,
  options: StreamingOptions,
): Promise<ChatApiResponse> {
  const { includeCommentary, conversationId, deviceId, onProgress } = options;
  const response = await fetch(apiPath('v1/chat'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify({ message, includeCommentary, conversationId, deviceId }),
  });

  if (!response.ok) {
    if (response.status === 429) throw new QueueFullError();
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `Chat API error: ${response.status}`);
  }

  let finalResponse: ChatApiResponse | null = null;
  await parseSSEStream(response.body!.getReader(), (event) => {
    onProgress(event);
    if (event.type === 'complete') finalResponse = event as unknown as ChatApiResponse;
    if (event.type === 'scripture-response') {
      finalResponse = {
        response: '',
        intent: event.intent,
        sources: { verseCount: event.data.verses.length, commentaryCount: 0, commentarySources: [] },
      };
    }
    if (event.type === 'search-results') {
      finalResponse = {
        response: `Found ${event.total} results for "${event.query}" in ${event.bookName}`,
        intent: { intent: 'book_search', confidence: 1.0 },
        sources: { verseCount: event.total, commentaryCount: 0, commentarySources: [] },
      };
    }
    if (event.type === 'locator-results') {
      finalResponse = {
        response: event.results.map((r) => r.sentence).join('\n\n'),
        intent: { intent: 'locator', confidence: 1.0 },
        sources: { verseCount: event.results.length, commentaryCount: 0, commentarySources: [] },
      };
    }
    if (event.type === 'queue' && event.state === 'full') throw new QueueFullError();
    if (event.type === 'error') throw new Error(event.message);
  });

  if (!finalResponse) throw new Error('Stream ended without complete event');
  return finalResponse;
}

/**
 * Fetch on-demand ML commentary for a scripture-first response.
 */
export async function fetchCommentary(
  verseRefs: string[],
  originalQuery: string,
): Promise<{ response: string }> {
  const response = await fetch(apiPath('v1/commentary'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verseRefs, originalQuery }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `Commentary API error: ${response.status}`);
  }

  return (await response.json()) as { response: string };
}

/**
 * Fetch commentary via SSE for real-time pipeline progress.
 */
export interface CommentaryStreamResult {
  response: string;
  rawModelBody?: string;
  /** The prompt this run sent to the model — shown in the report. */
  prompt?: string;
  editReceipt?: ReceiptItem[];
  /** V6.4 — the expanded passage's per-verse KJV for verse-anchor labels. */
  passageVerses?: Array<{ verse: number; text: string }>;
  /** What the run did — duration, LLMs called, guard/validator verdicts.
   *  Printed as the report's Metadata block. */
  runMeta?: CommentaryRunMeta;
}

export async function fetchCommentaryStreaming(
  verseRefs: string[],
  originalQuery: string,
  onProgress: (event: ChatEvent) => void,
  opts: { mode?: 'verse' | 'pericope'; readerThoughts?: string } = {},
): Promise<CommentaryStreamResult> {
  const response = await fetch(apiPath('v1/commentary'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify({
      verseRefs,
      originalQuery,
      ...(opts.mode ? { mode: opts.mode } : {}),
      ...(opts.readerThoughts ? { readerThoughts: opts.readerThoughts } : {}),
    }),
  });

  if (!response.ok) {
    if (response.status === 429) throw new QueueFullError();
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `Commentary API error: ${response.status}`);
  }

  let finalResponse: CommentaryStreamResult | null = null;
  await parseSSEStream(response.body!.getReader(), (event) => {
    onProgress(event);
    if (event.type === 'complete') {
      const e = event as unknown as CommentaryStreamResult;
      finalResponse = {
        response: e.response,
        rawModelBody: e.rawModelBody,
        prompt: e.prompt,
        editReceipt: e.editReceipt,
        passageVerses: e.passageVerses,
        runMeta: e.runMeta,
      };
    }
    if (event.type === 'queue' && event.state === 'full') throw new QueueFullError();
    if (event.type === 'error') throw new Error(event.message);
  });

  if (!finalResponse) throw new Error('Stream ended without complete event');
  return finalResponse;
}

/**
 * Check pipeline readiness — calls GET /v1/ping.
 */
export async function fetchPing(): Promise<PingResponse> {
  const response = await fetch(apiPath('v1/ping'));
  if (!response.ok) throw new Error(`Ping failed: ${response.status}`);
  return (await response.json()) as PingResponse;
}
